import Vue from 'vue'

/**
 * Upload size limits for the Documents API (DOCS_API_URL).
 *
 * In agent-dev, DOCS_API_URL points at AWS API Gateway + Lambda, either via the
 * raw 'execute-api.' URL or the custom domain 'documents-agent-dev.responsegrid.com'.
 * Lambda caps request payloads at 6 MB and API Gateway base64-encodes binary
 * bodies (~33% overhead), so single files over ~4.5 MB are rejected (HTTP 413,
 * or a CORS-less 413 the browser reports as a network error).
 * Dev/test/prod run the Documents API on Elastic Beanstalk and are not affected.
 */
export const LAMBDA_UPLOAD_LIMIT_BYTES = 4.5 * 1024 * 1024

export function isLambdaDocsApi () {
  const url = String(process.env.DOCS_API_URL || '')
  return url.indexOf('execute-api.') !== -1 || url.indexOf('documents-agent-dev.') !== -1
}

function toMb (bytes) {
  return (bytes / (1024 * 1024)).toFixed(1)
}

function explain (file) {
  const name = (file && file.name) || 'The selected file'
  const size = file && typeof file.size === 'number' ? ` (${toMb(file.size)} MB)` : ''
  return `"${name}"${size} is too large to upload in this test environment. ` +
    'The agent-dev environment routes uploads through AWS Lambda, which has a hard 6 MB request limit. ' +
    'Uploads are base64-encoded on the way in (about +33%), so the effective maximum is about 4.5 MB per file. ' +
    'This is a limitation of the test environment, not a bug in this feature, and it does not happen on dev, test or production. ' +
    'To continue, use a file under 4.5 MB (e.g. resize or compress the image), or test large files on the test environment.'
}

/** Returns null if the file can be uploaded, otherwise a human-readable explanation. */
export function checkUploadSize (file) {
  if (!isLambdaDocsApi() || !file || typeof file.size !== 'number') return null
  return file.size > LAMBDA_UPLOAD_LIMIT_BYTES ? explain(file) : null
}

/** Returns the first size-limit explanation for a list of files, or null. */
export function checkUploadSizes (files) {
  const list = Array.from(files || [])
  for (let i = 0; i < list.length; i++) {
    const message = checkUploadSize(list[i])
    if (message) return message
  }
  return null
}

/** Returns the size-limit explanation if the upload error was caused by the payload limit, else null. */
export function describeUploadError (error, file) {
  const response = error && error.response
  if (response && response.status === 413) return explain(file)
  if (isLambdaDocsApi() && !response && file && file.size > LAMBDA_UPLOAD_LIMIT_BYTES) return explain(file)
  return null
}

/** Returns the largest file of a list (used to describe multi-file upload failures). */
export function largestFile (files) {
  return Array.from(files || []).reduce((max, f) => (!max || (f && f.size > max.size) ? f : max), null)
}

/** Shows the explanation as an error toast that stays until the user closes it. */
export function notifyUploadLimit (message) {
  Vue.prototype.$toasted.error(message, {
    duration: null,
    position: 'top-center',
    action: { text: 'Close', onClick: (e, toast) => toast.goAway(0) }
  })
}
